import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { hasFunctionAccess, hasDomainAccess, firstAccessiblePath } from './permissionConfig';
import { LockedFunctionPage } from './components/AccessLocked';
import { API_BASE } from './config';
import { TutorialProvider } from './components/tutorial/TutorialContext';
import TutorialSpotlight from './components/tutorial/TutorialSpotlight';
import TutorialWelcomeModal from './components/tutorial/TutorialWelcomeModal';
import Login                from './pages/Login';
import Landing              from './pages/Landing';
import DMSLayout            from './layouts/DMSLayout';
import PlatformLayout       from './layouts/PlatformLayout';
import SalesLayout          from './layouts/SalesLayout';
import InventoryLayout      from './layouts/InventoryLayout';
import POSOLifecycleLayout  from './layouts/POSOLifecycleLayout';
import D2CLayout             from './layouts/D2CLayout';
import Dashboard            from './pages/SalesDashboard';
import FestiveSeasonTrends  from './pages/FestiveSeasonTrends';
import StockDashboard       from './pages/StockDashboard';
import CardInventory        from './pages/CardInventory';
import InwardForm           from './pages/InwardForm';
import OutwardForm          from './pages/OutwardForm';
import SKUDescription       from './pages/SKUDescription';
import B2BDispatch          from './pages/B2BDispatch';
import SoKamReview          from './pages/SoKamReview';
import GRNManagement        from './pages/GRNManagement';
import RTVRecord            from './pages/RTVRecord';
import PackedBoxImages      from './pages/PackedBoxImages';
import SupplyChainDashboard from './pages/SupplyChainDashboard';
import POTracker            from './pages/POTracker';
import DistributorPO        from './pages/DistributorPO';
import DistributorPOHistory from './pages/DistributorPOHistory';
import DistributorMaster    from './pages/DistributorMaster';
import DistributorMasterRows from './pages/DistributorMasterRows';
import DistributorMasterSkus from './pages/DistributorMasterSkus';
import ProformaInvoice from './pages/ProformaInvoice';
import InstitutionalOrders from './pages/InstitutionalOrders';
import GiftingDispatch from './pages/GiftingDispatch';
import PORegisterData from './pages/PORegisterData';
import DMSUprisingInventory from './pages/DMSUprisingInventory';
import FatehpuriaInventory from './pages/FatehpuriaInventory';
import ChoudharyInventory from './pages/ChoudharyInventory';
import AdminConsole         from './pages/AdminConsole';
import PlatformAvailability from './pages/PlatformAvailability';
import AccountsLayout from './layouts/AccountsLayout';
import SalesOrders from './pages/SalesOrders';
import AccountsInvoiceQueue from './pages/AccountsInvoiceQueue';
import Invoices from './pages/Invoices';
import SoVsInvoice from './pages/SoVsInvoice';
import WarehouseHome from './layouts/WarehouseHome';
import SOInvoiceCheck from './pages/SOInvoiceCheck';
import DevConsole from './pages/DevConsole';
import ShopifyTracker from './pages/ShopifyTracker';
import PodByHandUpload from './pages/PodByHandUpload';
import { EditAccessToastHost } from './components/EditAccessToast';
import { UpdateBannerHost } from './components/UpdateBanner';
import { UrgentApprovalBannerHost } from './components/UrgentApprovalBanner';
import HelpChatbot from './components/HelpChatbot';
import ActivityTracker from './components/ActivityTracker';
import './App.css';

export default function App() {
    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem('sf_user');
        return saved ? JSON.parse(saved) : null;
    });

    function handleLogin(u, password) {
        localStorage.setItem('sf_user', JSON.stringify(u));
        if (u.role === 'admin') localStorage.setItem('sf_pass', password);
        setUser(u);
    }

    function handleLogout() {
        localStorage.removeItem('sf_user');
        localStorage.removeItem('sf_pass');
        setUser(null);
    }

    const [showTutorialPrompt, setShowTutorialPrompt] = useState(false);

    function mergeFreshPermissions(data) {
        setUser((prev) => {
            if (!prev) return prev;
            const merged = {
                ...prev,
                role: data.role,
                display_name: data.display_name,
                function_permissions: data.function_permissions,
                edit_permissions: data.edit_permissions,
                notifications_enabled: data.notifications_enabled,
            };
            localStorage.setItem('sf_user', JSON.stringify(merged));
            return merged;
        });
    }

    // Once per browser session (not per refresh — sessionStorage clears when
    // the tab/browser closes, not on reload): log that the portal was
    // opened, bump the onboarding-tutorial auto-prompt counter, and pick up
    // a fresh permission snapshot in the same round trip.
    useEffect(() => {
        if (!user?.username) return;
        if (sessionStorage.getItem('sf_session_opened')) return;
        sessionStorage.setItem('sf_session_opened', '1');

        fetch(`${API_BASE}/auth?action=record_session_open`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: user.username }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (!data.success) {
                    if (data.reason === 'inactive') handleLogout();
                    return;
                }
                mergeFreshPermissions(data);
                if (data.should_auto_prompt) setShowTutorialPrompt(true);
            })
            .catch(() => { /* best-effort — a failed session-open just skips the tutorial prompt this time */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.username]);

    // The actual fix for "permission changes need logout/login": sf_user is
    // otherwise only ever read from localStorage once at App mount and never
    // refreshed. Polls every 60s (matching NotificationCenter.jsx's existing
    // POLL_MS=60000 convention) plus once immediately on tab focus, so an
    // admin's permission edit shows up here on its own — and a plain page
    // refresh picks it up even faster, since this effect re-runs on mount too.
    useEffect(() => {
        if (!user?.username) return;
        let cancelled = false;

        async function refresh() {
            try {
                const res = await fetch(`${API_BASE}/auth?action=refresh_permissions&username=${encodeURIComponent(user.username)}`);
                const data = await res.json();
                if (cancelled) return;
                if (!data.success) {
                    if (data.reason === 'inactive') handleLogout();
                    return;
                }
                mergeFreshPermissions(data);
            } catch (e) { /* network hiccup — next tick or the next focus event will retry */ }
        }

        refresh();
        const interval = setInterval(refresh, 60000);
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            cancelled = true;
            clearInterval(interval);
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.username]);

    if (!user) return <Login onLogin={handleLogin} />;

    // Warehouse role — redirect immediately to their dedicated page.
    // Still gated on the 'warehouse_access' function (not just the role
    // label) so a warehouse-role user's access can be revoked from the
    // Admin Console without changing their role.
    if (user.role === 'warehouse') {
    return (
        <BrowserRouter>
            <ActivityTracker user={user} />
            <EditAccessToastHost />
            <UpdateBannerHost />
            <Routes>
                <Route path="/warehouse" element={
                    hasFunctionAccess(user, 'warehouse', 'warehouse_access')
                        ? <WarehouseHome user={user} onLogout={handleLogout} />
                        : <Login onLogin={handleLogin} />
                } />
                <Route path="*" element={<Navigate to="/warehouse" />} />
            </Routes>
        </BrowserRouter>
    );
}

    return (
        <BrowserRouter>
            <ActivityTracker user={user} />
            <EditAccessToastHost />
            <UpdateBannerHost />
            <UrgentApprovalBannerHost user={user} />
            <HelpChatbot user={user} />
            <TutorialProvider user={user}>
            <TutorialWelcomeModal
                open={showTutorialPrompt}
                displayName={user.display_name || user.username}
                onClose={() => setShowTutorialPrompt(false)}
            />
            <TutorialSpotlight />
            <Routes>

                {/* Default redirect */}
                <Route path="/" element={
                    <Navigate to={user.role === 'admin' ? '/admin' : '/landing'} />
                } />

                {/* Landing */}
                <Route path="/landing" element={
                    <Landing user={user} onLogout={handleLogout} />
                } />

                {/* Admin */}
                <Route path="/admin" element={
                    user.role === 'admin'
                        ? <AdminConsole user={user} onLogout={handleLogout} />
                        : <Navigate to="/landing" />
                } />

                {/* Sales routes — domain-gated: any Sales function grants entry to
                    the layout/sidebar; the specific page shown is locked per-function. */}
                <Route path="/sales" element={
                    hasDomainAccess(user, 'sales')
                        ? <SalesLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'sales', 'dashboard') ? <Dashboard user={user} /> : <LockedFunctionPage label="Sales Dashboard" />}</SalesLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/sales/festive-season-trends" element={
                    hasDomainAccess(user, 'sales')
                        ? <SalesLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'sales', 'festive_season_trends') ? <FestiveSeasonTrends user={user} /> : <LockedFunctionPage label="Festive Season Trends" />}</SalesLayout>
                        : <Navigate to="/landing" />
                } />

                {/* Inventory routes — domain-gated (see Sales comment above) */}
                <Route path="/inventory" element={
                    hasDomainAccess(user, 'inventory')
                        ? <InventoryLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'inventory', 'stock_dashboard') ? <StockDashboard user={user} /> : <LockedFunctionPage label="Stock Dashboard" />}</InventoryLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/inventory/inward" element={
                    hasDomainAccess(user, 'inventory')
                        ? <InventoryLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'inventory', 'inward') ? <InwardForm user={user} /> : <LockedFunctionPage label="Inward Entry" />}</InventoryLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/inventory/outward" element={
                    hasDomainAccess(user, 'inventory')
                        ? <InventoryLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'inventory', 'outward') ? <OutwardForm user={user} /> : <LockedFunctionPage label="Outward Entry" />}</InventoryLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/inventory/sku-desc" element={
                    hasDomainAccess(user, 'inventory')
                        ? <InventoryLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'inventory', 'sku_master') ? <SKUDescription user={user} /> : <LockedFunctionPage label="SKU Master" />}</InventoryLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/inventory/cards" element={
                    hasDomainAccess(user, 'inventory')
                        ? <InventoryLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'inventory', 'card_inventory') ? <CardInventory user={user} /> : <LockedFunctionPage label="Card Inventory" />}</InventoryLayout>
                        : <Navigate to="/landing" />
                } />

                {/* Invoicing routes — reachable from Landing for permitted roles.
                    Same underlying components as Baljinder's dedicated /accounts/*
                    flow above, but a distinct URL prefix so the two entry points
                    don't collide. Domain-gated (see Sales comment above). */}
                <Route path="/invoicing" element={<Navigate to="/invoicing/invoice-queue" replace />} />
                {/* Sales Orders — replaces the former /so-queue and /cancel-so
                    pages, which were near-duplicates. Page access is gated on
                    the existing `so_queue` key (so nobody's saved permissions
                    needed migrating); the Cancel button inside is separately
                    gated on `cancel_so`. */}
                <Route path="/invoicing/sales-orders" element={
                    hasDomainAccess(user, 'invoicing')
                        ? <AccountsLayout user={user} onLogout={handleLogout} basePath="/invoicing">{hasFunctionAccess(user, 'invoicing', 'so_queue') ? <SalesOrders user={user} /> : <LockedFunctionPage label="Sales Orders" />}</AccountsLayout>
                        : <Navigate to="/landing" />
                } />
                {/* Old routes kept as redirects so already-sent notification
                    deep-links (and any bookmarks) still land somewhere useful
                    instead of 404ing. */}
                <Route path="/invoicing/so-queue" element={<Navigate to="/invoicing/sales-orders" replace />} />
                <Route path="/invoicing/cancel-so" element={<Navigate to="/invoicing/sales-orders" replace />} />
                <Route path="/invoicing/invoice-queue" element={
                    hasDomainAccess(user, 'invoicing')
                        ? <AccountsLayout user={user} onLogout={handleLogout} basePath="/invoicing">{hasFunctionAccess(user, 'invoicing', 'invoice_queue') ? <AccountsInvoiceQueue user={user} /> : <LockedFunctionPage label="Invoice Queue" />}</AccountsLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/invoicing/distributor-performa-invoice" element={
                    hasDomainAccess(user, 'invoicing')
                        ? <AccountsLayout user={user} onLogout={handleLogout} basePath="/invoicing">{hasFunctionAccess(user, 'invoicing', 'distributor_performa_invoice') ? <ProformaInvoice user={user} /> : <LockedFunctionPage label="Proforma Invoice" />}</AccountsLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/invoicing/invoices" element={
                    hasDomainAccess(user, 'invoicing')
                        ? <AccountsLayout user={user} onLogout={handleLogout} basePath="/invoicing">{hasFunctionAccess(user, 'invoicing', 'invoices') ? <Invoices user={user} /> : <LockedFunctionPage label="Invoices" />}</AccountsLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/invoicing/so-vs-invoice" element={
                    hasDomainAccess(user, 'invoicing')
                        ? <AccountsLayout user={user} onLogout={handleLogout} basePath="/invoicing">{hasFunctionAccess(user, 'invoicing', 'so_vs_invoice') ? <SoVsInvoice user={user} /> : <LockedFunctionPage label="SO vs Invoice" />}</AccountsLayout>
                        : <Navigate to="/landing" />
                } />

                {/* PO & SO Lifecycle routes — domain-gated (see Sales comment above) */}
                <Route path="/po-so-lifecycle" element={<Navigate to={firstAccessiblePath(user, 'po_so_lifecycle') || '/landing'} />} />
                <Route path="/po-so-lifecycle/supply-chain-dashboard" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'supply_chain_dashboard') ? <SupplyChainDashboard user={user} /> : <LockedFunctionPage label="Supply Chain Analytics Dashboard" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/po-tracker" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'po_tracker') ? <POTracker user={user} /> : <LockedFunctionPage label="PO Tracker" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/b2b-dispatch" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'b2b_dispatch') ? <B2BDispatch user={user} /> : <LockedFunctionPage label="B2B Dispatch" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/so-kam-review" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'so_kam_review') ? <SoKamReview user={user} /> : <LockedFunctionPage label="SO KAM Review" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/grn-management" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'grn_management') ? <GRNManagement user={user} /> : <LockedFunctionPage label="Goods Received Note" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/rtv-record" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'rtv_record') ? <RTVRecord user={user} /> : <LockedFunctionPage label="RTV Record" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/box-images" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'box_images') ? <PackedBoxImages user={user} /> : <LockedFunctionPage label="Packed Box Images" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/institutional-orders" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'institutional_orders') ? <InstitutionalOrders user={user} /> : <LockedFunctionPage label="Institutional Orders" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/gifting-dispatch" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'gifting_dispatch') ? <GiftingDispatch user={user} /> : <LockedFunctionPage label="Gifting & Dispatch" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/po-so-lifecycle/po-register-data" element={
                    hasDomainAccess(user, 'po_so_lifecycle')
                        ? <POSOLifecycleLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'po_so_lifecycle', 'po_register_data') ? <PORegisterData user={user} /> : <LockedFunctionPage label="PO Register Data" />}</POSOLifecycleLayout>
                        : <Navigate to="/landing" />
                } />

                {/* Personal diagnostic tool — checks whether a real invoice exists for
                    an SO by matching PO/order ref no. Not linked from any nav/tile/
                    permission config, reachable only by navigating directly to
                    /so-invoice-check. */}
                <Route path="/so-invoice-check" element={
                    user.role === 'admin'
                        ? <SOInvoiceCheck user={user} onLogout={handleLogout} />
                        : <Navigate to="/landing" />
                } />

                {/* Personal developer console — raw CRUD editor for SO/Invoice data,
                    manual SO qty validation (replaces the old manual-so-validate
                    stand-in), and auto-loaded discrepancies. Admin-only, not linked
                    from any nav/tile/permission config. */}
                <Route path="/dev-console" element={
                    user.role === 'admin'
                        ? <DevConsole user={user} onLogout={handleLogout} />
                        : <Navigate to="/landing" />
                } />

                {/* DMS routes */}
                <Route path="/dms" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'uprising_inventory') ? <DMSUprisingInventory user={user} /> : <LockedFunctionPage label="Uprising Inventory" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/dms/fatehpuria-inventory" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'fatehpuria_inventory') ? <FatehpuriaInventory user={user} /> : <LockedFunctionPage label="Fatehpuria Inventory" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/dms/choudhary-inventory" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'choudhary_inventory') ? <ChoudharyInventory user={user} /> : <LockedFunctionPage label="Choudhary Inventory" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/dms/distributor-po" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'distributor_po') ? <DistributorPO user={user} /> : <LockedFunctionPage label="Distributor Purchase Orders" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/dms/distributor-po/history" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'distributor_po') ? <DistributorPOHistory user={user} /> : <LockedFunctionPage label="Distributor Purchase Orders" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/dms/distributor-master" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'distributor_master') ? <DistributorMaster user={user} /> : <LockedFunctionPage label="Distributor Master" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/dms/distributor-master/distributors" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'distributor_master') ? <DistributorMasterRows user={user} /> : <LockedFunctionPage label="Distributor Master" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                <Route path="/dms/distributor-master/skus" element={
                    hasDomainAccess(user, 'dms')
                        ? <DMSLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'dms', 'distributor_master') ? <DistributorMasterSkus user={user} /> : <LockedFunctionPage label="Distributor Master" />}</DMSLayout>
                        : <Navigate to="/landing" />
                } />
                {/* Old location before this page moved from PO & SO Lifecycle to DMS. */}
                <Route path="/po-so-lifecycle/distributor-po" element={<Navigate to="/dms/distributor-po" />} />

                {/* Platform routes — whole-page function (SKU Availability) */}
                <Route path="/platform/availability" element={
                    hasFunctionAccess(user, 'platform', 'sku_availability')
                        ? <PlatformLayout user={user} onLogout={handleLogout}><PlatformAvailability user={user} /></PlatformLayout>
                        : <Navigate to="/landing" />
                } />

                {/* D2C routes */}
                <Route path="/d2c" element={<Navigate to={firstAccessiblePath(user, 'd2c') || '/landing'} />} />
                <Route path="/d2c/shopify-tracker" element={
                    hasDomainAccess(user, 'd2c')
                        ? <D2CLayout user={user} onLogout={handleLogout}>{hasFunctionAccess(user, 'd2c', 'shopify_tracker') ? <ShopifyTracker user={user} /> : <LockedFunctionPage label="Shopify Tracker" />}</D2CLayout>
                        : <Navigate to="/landing" />
                } />

                {/* POD Upload — standalone mobile page, no desktop layout chrome
                    (same bypass as /warehouse), just a permission gate. */}
                <Route path="/pod-upload" element={
                    hasFunctionAccess(user, 'pod_upload', 'pod_upload_access')
                        ? <PodByHandUpload user={user} onLogout={handleLogout} />
                        : <Navigate to="/landing" />
                } />

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/landing" />} />

            </Routes>
            </TutorialProvider>
        </BrowserRouter>
    );
}